import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, HelpCircle, Search, Book, MessageCircle, Star, Zap } from "lucide-react";


interface HelpCenterProps {
  onBack: () => void;
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onCreateNew: () => void;
  user?: {
    email?: string;
    name?: string;
  };
}

export const HelpCenter = ({ 
  onBack, 
  onNavigateToChat, 
  onNavigateToMyBoyfriends, 
  onNavigateToProfile, 
  onNavigateToSettings,
  onCreateNew,
  user 
}: HelpCenterProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const helpCategories = [
    {
      title: "Getting Started",
      icon: Star,
      color: "text-blue-600",
      articles: [
        { title: "How to create your first AI boyfriend", views: "2.1k" },
        { title: "Understanding personality types", views: "1.8k" },
        { title: "Customizing appearance and style", views: "1.5k" },
        { title: "Starting your first conversation", views: "1.2k" },
      ]
    },
    {
      title: "Premium Features",
      icon: Zap,
      color: "text-primary",
      articles: [
        { title: "What's included in Premium?", views: "3.2k" },
        { title: "How to upgrade to Premium", views: "2.8k" },
        { title: "Managing your subscription", views: "1.9k" },
        { title: "Exclusive Premium characters", views: "1.6k" },
      ]
    },
    {
      title: "Conversations",
      icon: MessageCircle,
      color: "text-green-600",
      articles: [
        { title: "How AI conversations work", views: "2.5k" },
        { title: "Improving conversation quality", views: "2.1k" },
        { title: "Voice messages and audio", views: "1.7k" },
        { title: "Sharing photos and media", views: "1.4k" },
      ]
    },
    {
      title: "Account & Privacy",
      icon: Book,
      color: "text-purple-600",
      articles: [
        { title: "Privacy and data protection", views: "1.9k" },
        { title: "Managing your account", views: "1.6k" },
        { title: "Deleting conversations", views: "1.3k" },
        { title: "Export your data", views: "1.1k" },
      ]
    }
  ];

  const filteredCategories = helpCategories.map(category => ({
    ...category,
    articles: category.articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.articles.length > 0);

  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Main Content */}
      <div className="p-4 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-primary/20">
            <Button variant="ghost" size="icon" onClick={onNavigateToProfile}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
              Help Center
            </h1>
          </div>

          {/* Search */}
          <Card className="p-6 mb-6 border-primary/20">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search for help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </Card>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Card className="p-4 border-primary/20 hover:border-primary/40 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <Book className="h-8 w-8 text-blue-600" />
                <div>
                  <h3 className="font-semibold">User Guide</h3>
                  <p className="text-sm text-muted-foreground">Complete user manual</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 border-primary/20 hover:border-primary/40 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <Star className="h-8 w-8 text-yellow-600" />
                <div>
                  <h3 className="font-semibold">Feature Requests</h3>
                  <p className="text-sm text-muted-foreground">Suggest new features</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Help Categories */}
          <div className="space-y-6">
            {(searchQuery ? filteredCategories : helpCategories).map((category) => (
              <Card key={category.title} className="p-6 border-primary/20">
                <div className="flex items-center gap-2 mb-4">
                  <category.icon className={`h-5 w-5 ${category.color}`} />
                  <h3 className="font-semibold">{category.title}</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {category.articles.map((article) => (
                    <div 
                      key={article.title}
                      className="flex items-center justify-between p-3 rounded-lg bg-card/50 hover:bg-card/70 transition-colors cursor-pointer group"
                    >
                      <div className="flex-1">
                        <h4 className="font-medium group-hover:text-primary transition-colors">
                          {article.title}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {article.views} views
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                        →
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          {/* No Results */}
          {searchQuery && filteredCategories.length === 0 && (
            <Card className="p-8 text-center border-primary/20">
              <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">No articles found</h3>
              <p className="text-muted-foreground mb-4">
                We couldn't find any articles matching "{searchQuery}"
              </p>
              <Button variant="romantic" onClick={() => setSearchQuery("")}>
                View All Articles
              </Button>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
};